import type { RequestType } from '@dia/db';

export const REQUEST_TYPES: readonly RequestType[] = [
  'boat',
  'provisioning',
  'maintenance',
  'housekeeping',
  'concierge',
  'other',
] as const;

/** Types where "when" is a real question. Something broken is "now". */
export const TYPES_WITH_TIME: ReadonlySet<RequestType> = new Set<RequestType>([
  'boat',
  'provisioning',
  'housekeeping',
  'concierge',
]);

/**
 * A datetime-local input gives "2026-10-04T16:30" with no zone. The island is
 * always UTC minus five, so the instant is that string with -05:00 on it.
 * Returns null for anything that is not that shape, so a half typed value never
 * becomes a due date.
 */
export function panamaLocalToIso(local: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return null;
  const at = new Date(`${local}:00-05:00`);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

export type RequestDraft = {
  type: RequestType;
  text: string;
  /** datetime-local string, or empty. */
  when: string;
};

export type RequestRowInput = {
  draft: RequestDraft;
  lang: 'es' | 'en';
  stayId: string;
  /** The translated shadow when we had signal to get one. */
  translation?: { lang: 'es' | 'en'; text: string } | null;
  /**
   * Paths in request-photos for a problem report. The photos upload on their
   * own the moment they are taken; the row only names where they will be.
   */
  photoPaths?: string[];
};

/**
 * The row that goes to the outbox. Guests write their own request, so
 * created_by_guest is true and stay_id is theirs, which is exactly what the
 * insert policy checks. The original is always kept, and the shadow is filled
 * when the translate function answered.
 */
export function buildRequestRow(input: RequestRowInput): Record<string, unknown> {
  const text = input.draft.text.trim();
  const row: Record<string, unknown> = {
    stay_id: input.stayId,
    created_by_guest: true,
    type: input.draft.type,
    text_original: text,
    lang_original: input.lang,
    text_es: input.lang === 'es' ? text : null,
    text_en: input.lang === 'en' ? text : null,
  };
  if (input.translation && input.translation.lang !== input.lang) {
    row[input.translation.lang === 'es' ? 'text_es' : 'text_en'] = input.translation.text;
  }
  if (TYPES_WITH_TIME.has(input.draft.type)) {
    const due = panamaLocalToIso(input.draft.when);
    if (due) row.due_at = due;
  }
  if (input.photoPaths && input.photoPaths.length > 0) {
    row.photo_paths = [...input.photoPaths];
  }
  return row;
}
